import type { SpotlightForm } from "@redotech/redo-spotlight-common/spotlight-schema";
import { SpotlightTriggerType } from "@redotech/redo-spotlight-common/spotlight-schema";
import { assertNever } from "@redotech/util/type";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ANONYMOUS_SHOPPER_ID_KEY } from "../../utils/shared-conf";
import { isWithinFrequencyCap } from "./frequency-cap";
import { localizeSpotlight } from "./localize-spotlight";
import { SpotlightLauncher } from "./spotlight-launcher";
import { SpotlightModal } from "./spotlight-modal";
import { SpotlightInstanceProps, SpotlightLocalData } from "./types";
import {
  getNextStepIndex,
  isOnMobile,
  submitSpotlightStep,
  useLocalStorage,
} from "./utils";

export const SpotlightInstance = memo(function SpotlightInstance(
  props: SpotlightInstanceProps,
) {
  const [open, setOpen] = useState(false);
  const [hasEverBeenOpened, setHasEverBeenOpened] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [localData, setLocalData] = useLocalStorage<SpotlightLocalData>(
    props.localStorageKey,
    {},
  );

  const [spotlight, setSpotlight] = useState<SpotlightForm | null>(
    props.forceSpotlight ?? null,
  );
  const [spotlightId, setSpotlightId] = useState<string | null>(null);
  const [treatmentId, setTreatmentId] = useState<string | null>(null);
  const [skipPhoneSteps, setSkipPhoneSteps] = useState<boolean>(false);
  const [resolvedDiscounts, setResolvedDiscounts] = useState<
    Record<string, string> | undefined
  >();
  const [merchantPhoneNumber, setMerchantPhoneNumber] = useState<
    string | undefined
  >();

  const anonymousId = localStorage.getItem(ANONYMOUS_SHOPPER_ID_KEY);

  const forceOpen = props.forceId || props.forceSpotlight;
  // Tracks whether the forceOpen auto-open has already fired. Prevents
  // re-opening the modal if the merchant closes it (defensive — current
  // useEffect deps don't include `open`, but a future dep change could
  // accidentally reintroduce the auto-reopen bug).
  const hasForceOpenedRef = useRef(false);
  const client = props.rpcClient;

  const fetchSpotlight = useCallback(async (): Promise<{
    spotlight: SpotlightForm | null;
    spotlightId: string | null;
    treatmentId: string | null;
    skipPhoneSteps: boolean;
    merchantPhoneNumber?: string;
  }> => {
    if (!client || !anonymousId) {
      return {
        spotlight: null,
        spotlightId: null,
        treatmentId: null,
        skipPhoneSteps: false,
      };
    }

    try {
      const result = await client.getSpotlight({
        customerHash: anonymousId,
        shopifyCustomerId: props.shopifyCustomerId ?? null,
        isOnMobile: isOnMobile(),
        currentUrl: window.location.href,
        forceId: props.forceId,
        triggerType: props.triggerType,
        country: props.country,
      });

      return {
        spotlight: result.spotlight,
        spotlightId: result.spotlightId,
        treatmentId: result.treatmentId,
        skipPhoneSteps: result.skipPhoneSteps,
        merchantPhoneNumber: result.merchantPhoneNumber,
      };
    } catch (e) {
      console.error(
        "Failed to fetch spotlight for trigger",
        props.triggerType,
        e,
      );
      return {
        spotlight: null,
        spotlightId: null,
        treatmentId: null,
        skipPhoneSteps: false,
      };
    }
  }, [
    client,
    anonymousId,
    props.shopifyCustomerId,
    props.forceId,
    props.triggerType,
    props.country,
  ]);

  // Handle forced spotlight (for preview)
  useEffect(() => {
    if (props.forceSpotlight) {
      setSpotlight(props.forceSpotlight);
      // Auto-open the modal in preview/test-on-site mode so the merchant
      // (or QA harness) sees the modal immediately, without having to
      // click the teaser/tab launcher and without waiting for the
      // timeOnPage delay. The ref guard ensures we only auto-open once;
      // closing the modal must not trigger a re-open.
      if (!hasForceOpenedRef.current) {
        hasForceOpenedRef.current = true;
        setOpen(true);
      }
      return;
    }

    if (props.forceId && client && anonymousId) {
      client
        .getSpotlight({
          customerHash: anonymousId,
          shopifyCustomerId: props.shopifyCustomerId ?? null,
          isOnMobile: isOnMobile(),
          currentUrl: window.location.href,
          forceId: props.forceId,
          country: props.country,
        })
        .then((result) => {
          setSpotlight(result.spotlight);
          setSpotlightId(result.spotlightId);
          setTreatmentId(result.treatmentId);
          setSkipPhoneSteps(result.skipPhoneSteps);
          setMerchantPhoneNumber(result.merchantPhoneNumber);
          // Same auto-open rationale + once-only guard as above.
          if (!hasForceOpenedRef.current) {
            hasForceOpenedRef.current = true;
            setOpen(true);
          }
        })
        .catch((e) => console.error("Failed to fetch forced spotlight", e));
    }
  }, [
    props.forceSpotlight,
    props.forceId,
    client,
    anonymousId,
    props.shopifyCustomerId,
    props.country,
  ]);

  const shouldDisplay = !!spotlight;

  // State to hold the pre-fetched time-on-page spotlight config
  const [timeOnPageConfig, setTimeOnPageConfig] = useState<{
    spotlight: SpotlightForm;
    spotlightId: string | null;
    treatmentId: string | null;
    skipPhoneSteps: boolean;
    merchantPhoneNumber?: string;
  } | null>(null);

  const openModal = useCallback(async () => {
    if (open) {
      return;
    }

    let currentSpotlight = spotlight;
    let currentSpotlightId = spotlightId;
    let currentTreatmentId = treatmentId;

    let currentSkipPhoneSteps = skipPhoneSteps;

    if (!currentSpotlight && timeOnPageConfig) {
      currentSpotlight = timeOnPageConfig.spotlight;
      currentSpotlightId = timeOnPageConfig.spotlightId;
      currentTreatmentId = timeOnPageConfig.treatmentId;
      currentSkipPhoneSteps = timeOnPageConfig.skipPhoneSteps;
      setSpotlight(currentSpotlight);
      setSpotlightId(currentSpotlightId);
      setTreatmentId(currentTreatmentId);
      setSkipPhoneSteps(currentSkipPhoneSteps);
      setMerchantPhoneNumber(timeOnPageConfig.merchantPhoneNumber);
    }

    setOpen(true);
    setHasEverBeenOpened(true);
    setCurrentStep(
      getNextStepIndex(currentSpotlight, currentSkipPhoneSteps, 0),
    );

    if (
      !localData.spotlightViewedAt &&
      anonymousId &&
      client &&
      currentSpotlightId
    ) {
      client
        .logSpotlightAnalytics({
          anonymousId,
          shopifyCustomerId: props.shopifyCustomerId ?? null,
          treatmentId: currentTreatmentId,
          spotlightId: currentSpotlightId,
          type: "view",
        })
        .catch((e) => console.error("Failed to log spotlight view", e));
      setLocalData((prev: SpotlightLocalData) => ({
        ...prev,
        spotlightViewedAt: new Date(),
      }));
    }
  }, [
    open,
    localData,
    client,
    spotlightId,
    treatmentId,
    spotlight,
    skipPhoneSteps,
    timeOnPageConfig,
    props.shopifyCustomerId,
    setLocalData,
    anonymousId,
  ]);

  // ========== TIME_ON_PAGE trigger ==========

  // Phase 1: Fetch time-on-page spotlight to get its delay configuration
  useEffect(() => {
    if (props.triggerType !== SpotlightTriggerType.TIME_ON_PAGE) {
      return;
    }
    if (forceOpen || spotlight || timeOnPageConfig || !client || !anonymousId) {
      return;
    }

    fetchSpotlight()
      .then((result) => {
        if (result.spotlight) {
          setTimeOnPageConfig({
            spotlight: result.spotlight,
            spotlightId: result.spotlightId,
            treatmentId: result.treatmentId,
            skipPhoneSteps: result.skipPhoneSteps,
            merchantPhoneNumber: result.merchantPhoneNumber,
          });
        }
      })
      .catch((e) =>
        console.error("Failed to fetch time-on-page spotlight config", e),
      );
  }, [
    props.triggerType,
    forceOpen,
    spotlight,
    timeOnPageConfig,
    client,
    anonymousId,
    fetchSpotlight,
  ]);

  // Phase 2: Set up timer using the fetched spotlight's configured delay
  useEffect(() => {
    if (props.triggerType !== SpotlightTriggerType.TIME_ON_PAGE) {
      return;
    }
    if (!timeOnPageConfig || spotlight || completed || forceOpen) {
      return;
    }

    if (
      isWithinFrequencyCap({
        lastShownAt: localData.spotlightAutoOpenedAt,
        frequencyCapHours: timeOnPageConfig.spotlight.frequencyCapHours,
        now: Date.now(),
      })
    ) {
      return;
    }

    const delaySeconds = timeOnPageConfig.spotlight.triggers.timeOnPage.seconds;

    const timeout = setTimeout(() => {
      if (localData.spotlightAutoOpenedAt) {
        return;
      }

      setSpotlight(timeOnPageConfig.spotlight);
      setSpotlightId(timeOnPageConfig.spotlightId);
      setTreatmentId(timeOnPageConfig.treatmentId);
      setSkipPhoneSteps(timeOnPageConfig.skipPhoneSteps);
      setMerchantPhoneNumber(timeOnPageConfig.merchantPhoneNumber);
      setLocalData((prev: SpotlightLocalData) => ({
        ...prev,
        spotlightAutoOpenedAt: new Date(),
        spotlightId: timeOnPageConfig.spotlightId ?? undefined,
      }));
      setOpen(true);
      setHasEverBeenOpened(true);
      setCurrentStep(
        getNextStepIndex(
          timeOnPageConfig.spotlight,
          timeOnPageConfig.skipPhoneSteps,
          0,
        ),
      );

      if (timeOnPageConfig.spotlightId && client && anonymousId) {
        client
          .logSpotlightAnalytics({
            anonymousId,
            shopifyCustomerId: props.shopifyCustomerId ?? null,
            treatmentId: timeOnPageConfig.treatmentId,
            spotlightId: timeOnPageConfig.spotlightId,
            type: "view",
          })
          .catch((e) => console.error("Failed to log spotlight view", e));
      }
    }, delaySeconds * 1000);

    return () => clearTimeout(timeout);
  }, [
    props.triggerType,
    timeOnPageConfig,
    spotlight,
    completed,
    forceOpen,
    localData.spotlightAutoOpenedAt,
    anonymousId,
    client,
    setLocalData,
    props.shopifyCustomerId,
  ]);

  // ========== EXIT_INTENT trigger ==========

  useEffect(() => {
    if (props.triggerType !== SpotlightTriggerType.EXIT_INTENT) {
      return;
    }
    if (
      completed ||
      forceOpen ||
      open ||
      spotlight ||
      !client ||
      !anonymousId
    ) {
      return;
    }

    // The cap lives on the spotlight, which isn't fetched until the shopper
    // actually exit-intents. Gate on the cap recorded the last time this
    // trigger fired so an already-capped shopper costs no request; a cap edit
    // takes effect once that stored window elapses.
    if (
      isWithinFrequencyCap({
        lastShownAt: localData.lastTriggeredAt,
        frequencyCapHours: localData.lastTriggeredFrequencyCapHours,
        now: Date.now(),
      })
    ) {
      return;
    }

    let hasTriggered = false;
    let lastY = 0;
    let lastTime = Date.now();

    const triggerExitIntent = async () => {
      if (hasTriggered) {
        return;
      }
      hasTriggered = true;

      const result = await fetchSpotlight();

      if (result.spotlight) {
        setSpotlight(result.spotlight);
        setSpotlightId(result.spotlightId);
        setTreatmentId(result.treatmentId);
        setSkipPhoneSteps(result.skipPhoneSteps);
        setMerchantPhoneNumber(result.merchantPhoneNumber);
        setLocalData((prev: SpotlightLocalData) => ({
          ...prev,
          lastTriggeredAt: new Date(),
          lastTriggeredFrequencyCapHours:
            result.spotlight?.frequencyCapHours ?? undefined,
          spotlightId: result.spotlightId ?? undefined,
        }));
        setOpen(true);
        setHasEverBeenOpened(true);
        setCurrentStep(
          getNextStepIndex(result.spotlight, result.skipPhoneSteps, 0),
        );

        if (result.spotlightId && client && anonymousId) {
          client
            .logSpotlightAnalytics({
              anonymousId,
              shopifyCustomerId: props.shopifyCustomerId ?? null,
              treatmentId: result.treatmentId,
              spotlightId: result.spotlightId,
              type: "view",
            })
            .catch((e) => console.error("Failed to log spotlight view", e));
        }
      }
    };

    const handleMouseMove = (e: MouseEvent) => {
      const currentTime = Date.now();
      const timeDiff = currentTime - lastTime;
      const yDiff = e.clientY - lastY;

      const velocity = timeDiff > 0 ? yDiff / timeDiff : 0;

      lastY = e.clientY;
      lastTime = currentTime;

      if (e.clientY <= 50 && velocity < -0.5 && !hasTriggered) {
        triggerExitIntent().catch((e) =>
          console.error("Failed to open spotlight via exit intent", e),
        );
      }
    };

    const handleMouseLeave = (e: MouseEvent) => {
      if (e.clientY <= 20 && !hasTriggered) {
        triggerExitIntent().catch((e) =>
          console.error("Failed to open spotlight via exit intent", e),
        );
      }
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseleave", handleMouseLeave);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseleave", handleMouseLeave);
    };
  }, [
    props.triggerType,
    completed,
    forceOpen,
    open,
    spotlight,
    client,
    anonymousId,
    localData.lastTriggeredAt,
    localData.lastTriggeredFrequencyCapHours,
    fetchSpotlight,
    setLocalData,
    props.shopifyCustomerId,
  ]);

  // ========== BACK_IN_STOCK trigger ==========

  useEffect(() => {
    if (props.triggerType !== SpotlightTriggerType.BACK_IN_STOCK) {
      return;
    }
    if (completed || open || !client || !anonymousId) {
      return;
    }
    if (!props.backInStockContext) {
      return;
    }

    const { variantId } = props.backInStockContext;

    if (
      localData.lastBackInStockVariantId === variantId &&
      isWithinFrequencyCap({
        lastShownAt: localData.lastBackInStockTriggeredAt,
        frequencyCapHours: localData.lastBackInStockFrequencyCapHours,
        now: Date.now(),
      })
    ) {
      return;
    }

    if (spotlight) {
      setLocalData((prev: SpotlightLocalData) => ({
        ...prev,
        lastBackInStockTriggeredAt: new Date(),
        lastBackInStockVariantId: variantId,
        lastBackInStockFrequencyCapHours: spotlight.frequencyCapHours,
      }));
      setOpen(true);
      setHasEverBeenOpened(true);
      setCurrentStep(getNextStepIndex(spotlight, skipPhoneSteps, 0));
      return;
    }

    let cancelled = false;

    fetchSpotlight()
      .then((result) => {
        if (cancelled) {
          return;
        }
        if (result.spotlight) {
          const { frequencyCapHours } = result.spotlight;
          setSpotlight(result.spotlight);
          setSpotlightId(result.spotlightId);
          setTreatmentId(result.treatmentId);
          setSkipPhoneSteps(result.skipPhoneSteps);
          setMerchantPhoneNumber(result.merchantPhoneNumber);
          setLocalData((prev: SpotlightLocalData) => ({
            ...prev,
            lastBackInStockTriggeredAt: new Date(),
            lastBackInStockVariantId: variantId,
            lastBackInStockFrequencyCapHours: frequencyCapHours,
            spotlightId: result.spotlightId ?? undefined,
          }));
          setOpen(true);
          setHasEverBeenOpened(true);
          setCurrentStep(
            getNextStepIndex(result.spotlight, result.skipPhoneSteps, 0),
          );

          if (result.spotlightId && client && anonymousId) {
            client
              .logSpotlightAnalytics({
                anonymousId,
                shopifyCustomerId: props.shopifyCustomerId ?? null,
                treatmentId: result.treatmentId,
                spotlightId: result.spotlightId,
                type: "view",
              })
              .catch((e) => console.error("Failed to log spotlight view", e));
          }
        }
      })
      .catch((e) =>
        console.error("Failed to fetch back-in-stock spotlight", e),
      );

    return () => {
      cancelled = true;
    };
  }, [
    props.triggerType,
    props.backInStockContext,
    completed,
    open,
    spotlight,
    skipPhoneSteps,
    client,
    anonymousId,
    localData.lastBackInStockTriggeredAt,
    localData.lastBackInStockVariantId,
    localData.lastBackInStockFrequencyCapHours,
    fetchSpotlight,
    setLocalData,
    props.shopifyCustomerId,
  ]);

  // Apply the shopper-language translation overlay to the displayed copy.
  // localizeSpotlight is a no-op (returns the same reference) when there's no
  // language or no translations, so un-translated spotlights are unchanged.
  const localizedSpotlight = useMemo(
    () => (spotlight ? localizeSpotlight(spotlight, props.language) : null),
    [spotlight, props.language],
  );
  const localizedTimeOnPageSpotlight = useMemo(
    () =>
      timeOnPageConfig?.spotlight
        ? localizeSpotlight(timeOnPageConfig.spotlight, props.language)
        : null,
    [timeOnPageConfig?.spotlight, props.language],
  );

  const launcherConfig = useMemo(() => {
    if (localizedSpotlight?.launcher) {
      return localizedSpotlight.launcher;
    }
    if (localizedTimeOnPageSpotlight?.launcher?.showBeforePopupTriggered) {
      return localizedTimeOnPageSpotlight.launcher;
    }
    return null;
  }, [localizedSpotlight?.launcher, localizedTimeOnPageSpotlight?.launcher]);

  const showLauncher = useMemo(() => {
    if (forceOpen) {
      return true;
    }
    if (!launcherConfig) {
      return false;
    }
    if (localData.atLeastOneStepSubmittedAt) {
      return false;
    }
    switch (props.triggerType) {
      case SpotlightTriggerType.TIME_ON_PAGE:
        if (launcherConfig.showBeforePopupTriggered) {
          return !completed;
        }
        return hasEverBeenOpened && !completed;
      case SpotlightTriggerType.EXIT_INTENT:
        return hasEverBeenOpened && !completed;
      case SpotlightTriggerType.BACK_IN_STOCK:
        return props.isVariantOutOfStock === true;
      case SpotlightTriggerType.CHECKOUT_PROBABILITY:
      case SpotlightTriggerType.SHOPPER_INDECISION:
        return false;
      default:
        assertNever(props.triggerType);
    }
  }, [
    forceOpen,
    launcherConfig,
    localData.atLeastOneStepSubmittedAt,
    props.triggerType,
    props.isVariantOutOfStock,
    hasEverBeenOpened,
    completed,
  ]);

  useEffect(() => {
    props.onShouldDisplayChanged?.(shouldDisplay);
  }, [shouldDisplay, props]);

  const currentStepConfig = localizedSpotlight?.steps[currentStep];
  const isMobile = isOnMobile();

  if (!spotlight && !launcherConfig) {
    return null;
  }

  return (
    <>
      {launcherConfig && showLauncher && (
        <SpotlightLauncher
          brandKit={props.brandKit}
          launcher={launcherConfig}
          onClick={openModal}
          pdpAttachPoints={props.pdpAttachPoints}
          triggerType={props.triggerType}
        />
      )}
      {localizedSpotlight && (
        <SpotlightModal
          ariaLabel="Marketing spotlight"
          brandKit={props.brandKit}
          client={client}
          completed={completed}
          currentStep={currentStep}
          desktopConfig={currentStepConfig?.desktop}
          isMobile={isMobile}
          merchantPhoneNumber={merchantPhoneNumber}
          mobileConfig={currentStepConfig?.mobile}
          onClose={() => setOpen(false)}
          onSubmitStep={async (i: number, formData: Record<string, string>) => {
            const isFirstSubmission = !localData.atLeastOneStepSubmittedAt;
            const result = await submitSpotlightStep({
              i,
              formData,
              spotlight: localizedSpotlight,
              spotlightId,
              treatmentId,
              skipPhoneSteps,
              client,
              customerId: props.customerId,
              shopifyCustomerId: props.shopifyCustomerId,
              setCurrentStep,
              location: props.location,
              language: props.language,
              country: props.country,
              onLastStepSubmitted: () => {
                setOpen(false);
                setCompleted(true);
              },
            });
            if (isFirstSubmission) {
              setLocalData((prev: SpotlightLocalData) => ({
                ...prev,
                atLeastOneStepSubmittedAt: new Date(),
              }));
            }
            if (props.backInStockContext && props.onBackInStockSubmit) {
              props.onBackInStockSubmit(props.backInStockContext, formData);
            }
            if (result.resolvedDiscounts) {
              setResolvedDiscounts((prev) => ({
                ...prev,
                ...result.resolvedDiscounts,
              }));
            }
            return { resolvedDiscounts: result.resolvedDiscounts };
          }}
          open={open}
          resolvedDiscounts={resolvedDiscounts}
          spotlight={localizedSpotlight}
        />
      )}
    </>
  );
});
